/**
 * OUTFIT — Sovrn Commerce Service
 * Merchandise API : récupère produits ASOS + Zalando avec liens affiliés
 * Doc : https://developer.sovrn.com
 */

// v1.12.116 — clé renouvelée 2026-05 (compte mytenue.com)
const SOVRN_API_KEY    = '2ea6c250a81f2decfa3b9c05ab9617dd';
const SOVRN_SECRET_KEY = '332eeb50ce140d797c9b71b74a4ceaa48735874e';
const MERCHANDISE_API  = 'http://catalog.viglink.com/vigcatalog/products.xml';
const REDIRECT_API     = 'https://api.viglink.com/api/click';

// Marchands mode EU supportés
const SOVRN_MERCHANTS = {
  asos:    'asos.com',
  zalando: 'zalando.fr',
  hm:      'hm.com',
  amazon:  'amazon.fr',
  zara:    'zara.com',
};

const CATEGORY_KEYWORDS_F = {
  'Tout':        'vêtement mode',
  'Robes':       'robe',
  'Hauts':       'top blouse chemise',
  'Vestes':      'veste blazer manteau',
  'Bas':         'pantalon jupe jean',
  'Chaussures':  'chaussures',
  'Sacs':        'sac',
  'Accessoires': 'accessoires',
  'Bijoux':      'bijoux collier bracelet',
};

const CATEGORY_KEYWORDS_M = {
  'Tout':        'vêtement mode',
  // Pas de Robes pour homme
  'Hauts':       't-shirt chemise pull',
  'Vestes':      'veste blazer manteau',
  'Bas':         'pantalon jean short',
  'Chaussures':  'chaussures',
  'Sacs':        'sac sacoche',
  'Accessoires': 'accessoires ceinture',
};

// Legacy (par défaut F pour compat)
const CATEGORY_KEYWORDS = CATEGORY_KEYWORDS_F;

function getKeywords(category, gender) {
  const kwSet = gender === 'M' ? CATEGORY_KEYWORDS_M : CATEGORY_KEYWORDS_F;
  const kw = kwSet[category] || kwSet['Tout'];
  // Ajoute le suffixe gender pour bien orienter les résultats du marchand
  return kw + (gender === 'M' ? ' homme' : ' femme');
}

/**
 * Construire un lien affilié Sovrn depuis une URL produit
 */
export function buildSovrnAffiliateUrl(productUrl) {
  if (!productUrl) return null;
  return REDIRECT_API
    + '?key=' + SOVRN_API_KEY
    + '&u='   + encodeURIComponent(productUrl)
    + '&ref=' + encodeURIComponent('outfit-app');
}

/**
 * Récupérer les produits d'un marchand via Sovrn Merchandise API
 */
function parseVigLinkXML(xml) {
  try {
    const products = [];
    const productMatches = xml.match(/<product[^>]*>([\s\S]*?)<\/product>/g) || [];
    productMatches.forEach(block => {
      const get = (tag) => {
        const m = block.match(new RegExp('<' + tag + '[^>]*>([\s\S]*?)<\/' + tag + '>'));
        return m ? m[1].replace(/<!\[CDATA\[|\]\]>/g, '').trim() : '';
      };
      products.push({
        id:    get('id') || get('sku'),
        name:  get('name'),
        price: parseFloat(get('price_min') || get('price') || '0'),
        originalPrice: parseFloat(get('price_max') || get('price') || '0'),
        image: get('image'),
        description: get('description'),
        url:   get('url'),
        affiliateUrl: get('url'),
        brand: get('brand') || get('manufacturer'),
        category: get('category'),
        color: '',
        sizes: ['XS','S','M','L','XL'],
      });
    });
    return products;
  } catch(e) {
    console.warn('VigLink XML parse error:', e.message);
    return [];
  }
}

async function fetchSovrnProducts(merchant, keywords, limit = 20) {
  try {
    const url = MERCHANDISE_API
      + '?key='     + SOVRN_API_KEY
      + '&keyword=' + encodeURIComponent(keywords)
      + '&merchant='+ encodeURIComponent(merchant)
      + '&limit='   + limit;

    const res = await fetch(url, {
      headers: { 'Accept': 'application/xml, text/xml' },
    });

    if (!res.ok) {
      console.warn('Sovrn Catalog API error:', res.status, merchant);
      return [];
    }

    // Parser XML
    const xml  = await res.text();
    const items = parseVigLinkXML(xml);

    return items.map(item => ({
      id:           'sovrn_' + (item.id || item.sku || Math.random().toString(36).slice(2)),
      name:         item.name || item.title || '',
      brand:        item.brand || item.manufacturer || merchant.split('.')[0],
      price:        parseFloat(item.price || item.salePrice || 0),
      originalPrice:parseFloat(item.originalPrice || item.price || 0),
      image:        item.image || item.imageUrl || item.thumbnail || null,
      description:  item.description || '',
      category:     mapCategory(item.category || item.productType || ''),
      color:        item.color || '',
      sizes:        item.sizes || ['XS', 'S', 'M', 'L', 'XL'],
      partner:      merchant.includes('asos') ? 'asos'
                  : merchant.includes('zalando') ? 'zalando'
                  : merchant.includes('hm') ? 'hm'
                  : merchant.includes('amazon') ? 'amazon'
                  : merchant.split('.')[0],
      originalUrl:  item.url || item.link || '',
      affiliateUrl: item.affiliateUrl || buildSovrnAffiliateUrl(item.url || item.link),
      isAwin:       false,
      isSovrn:      true,
      source:       'sovrn',
      discount:     item.discount || null,
    })).filter(p => p.name && p.price > 0 && p.image);

  } catch(e) {
    console.warn('fetchSovrnProducts error:', e.message, merchant);
    return [];
  }
}

function mapCategory(raw) {
  const r = (raw || '').toLowerCase();
  if (r.includes('dress') || r.includes('robe'))           return 'Robes';
  if (r.includes('top') || r.includes('shirt') || r.includes('blouse')) return 'Hauts';
  if (r.includes('jacket') || r.includes('coat') || r.includes('veste')) return 'Vestes';
  if (r.includes('trouser') || r.includes('jean') || r.includes('skirt')) return 'Bas';
  if (r.includes('shoe') || r.includes('boot') || r.includes('sneaker')) return 'Chaussures';
  if (r.includes('bag') || r.includes('sac'))              return 'Sacs';
  if (r.includes('jewel') || r.includes('bijou'))          return 'Bijoux';
  if (r.includes('access'))                                return 'Accessoires';
  return 'Tout';
}

/**
 * Récupérer les produits ASOS via Sovrn
 */
export async function getSovrnAsosProducts(gender = 'F', category = 'Tout', limit = 20) {
  // Pour un homme qui demande "Robes" : on skip (pas de robes hommes)
  if (gender === 'M' && category === 'Robes') return [];
  const keywords = getKeywords(category, gender);
  return fetchSovrnProducts(SOVRN_MERCHANTS.asos, keywords, limit);
}

/**
 * Récupérer les produits Zalando via Sovrn
 */
export async function getSovrnZalandoProducts(gender = 'F', category = 'Tout', limit = 20) {
  if (gender === 'M' && category === 'Robes') return [];
  const keywords = getKeywords(category, gender);
  return fetchSovrnProducts(SOVRN_MERCHANTS.zalando, keywords, limit);
}

/**
 * Récupérer les produits H&M via Sovrn
 */
export async function getSovrnHMProducts(gender = 'F', category = 'Tout', limit = 20) {
  if (gender === 'M' && category === 'Robes') return [];
  const keywords = getKeywords(category, gender);
  return fetchSovrnProducts(SOVRN_MERCHANTS.hm, keywords, limit);
}

/**
 * Récupérer tous les produits mode via Sovrn (ASOS + Zalando + H&M en parallèle)
 */
export async function getSovrnAllProducts(gender = 'F', category = 'Tout', limitPerMerchant = 15) {
  const [asos, zalando, hm] = await Promise.allSettled([
    getSovrnAsosProducts(gender, category, limitPerMerchant),
    getSovrnZalandoProducts(gender, category, limitPerMerchant),
    getSovrnHMProducts(gender, category, limitPerMerchant),
  ]);

  const all = [
    ...(asos.value    || []),
    ...(zalando.value || []),
    ...(hm.value      || []),
  ];

  // Dédupliquer
  const seen = new Set();
  return all.filter(p => {
    if (seen.has(p.id)) return false;
    seen.add(p.id);
    return true;
  });
}
